import { useMemo, useState } from 'react';
import { AlertTriangle, Undo2 } from 'lucide-react';
import type { Project } from '../types/project';
import type { SuspectWindow } from '../lib/ami/suspectPeaks';
import { SUSPECT_PEAK_MESSAGE } from '../lib/ami/plausibilityValidator';
import {
  activeExclusions,
  CHECK_LABELS,
  DEFAULT_REASON,
  EXCLUSION_ACKNOWLEDGEMENT,
  EXCLUSION_REASONS,
  excludedHours,
  excludeWindow,
  exclusionsApply,
  MAX_EXCLUDED_HOURS,
  readingsInEffect,
  reasonLabel,
  removeExclusion,
  windowFullyExcluded,
  type ExclusionReason,
} from '../lib/peakExclusions';

/**
 * Flagged windows on the Existing Load tab: one amber card per window the
 * suspect-peak checks found, the form that excludes one, and the list of
 * exclusions in force with Undo.
 *
 * `unlocked` is the paywall (ux.md LOCKED #1). Locked, the panel says one
 * sentence — that a possible meter data error affects the peak — and nothing
 * about kilowatts, the time of the peak or what an exclusion would change.
 */

interface Props {
  project: Partial<Project>;
  /** Raw readings, as stored. */
  amiData: Array<{ timestamp: string; kw: number }>;
  windows: SuspectWindow[];
  unlocked: boolean;
  isReadOnly: boolean;
  onChange: (updates: Partial<Project>) => void;
  userId: string | null;
  userLabel: string;
}

/** Meter time (fixed-UTC wall clock), the way the peak tile, the chart and the report print times. */
function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC',
  });
}

function fmtHours(h: number): string {
  return Number.isInteger(h) ? `${h} h` : `${h.toFixed(1)} h`;
}

function explain(w: SuspectWindow, project: Partial<Project>): string {
  switch (w.check) {
    case 'gap_catchup':
      return `${Math.round(w.detail.gapHours ?? 0)} hours of missing readings, then readings far above the rest of the year. ` +
        'This usually means the meter stored usage and reported it all at once.';
    case 'exceeds_service_capacity':
      return `Readings above the ${(w.detail.serviceKw ?? 0).toFixed(1)} kW a ${project.service_rating_amps} A service can deliver. ` +
        'A service cannot carry more than its rating, so these cannot be the building’s demand.';
    case 'daily_energy_spike':
      return `This day used ${(w.detail.dayKwh ?? 0).toFixed(0)} kWh, more than three times the ${(w.detail.medianKwh ?? 0).toFixed(0)} kWh ` +
        'typical of the days around it. One day this far out of line usually comes from a meter or data error.';
    case 'utility_estimated':
      return 'The utility’s file marks these readings as estimated, not measured.';
  }
}

export function SuspectPeakPanel({ project, amiData, windows, unlocked, isReadOnly, onChange, userId, userLabel }: Props) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [reason, setReason] = useState<ExclusionReason>('meter_catchup');
  const [note, setNote] = useState('');
  const [ack, setAck] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const excluded = activeExclusions(project);
  const inEffect = useMemo(() => readingsInEffect(amiData, project), [amiData, project]);
  const open = windows.filter(
    w => !excluded.some(x => x.id === w.id) && !windowFullyExcluded(w, amiData, project)
  );

  if (open.length === 0 && excluded.length === 0) return null;

  if (!unlocked) {
    return (
      <div data-testid="suspect-peak-locked" className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <AlertTriangle className="mt-0.5 flex-shrink-0 text-amber-600" size={16} />
        <span>{SUSPECT_PEAK_MESSAGE}</span>
      </div>
    );
  }

  const canExclude = !isReadOnly && project.status !== 'completed';
  const rulesAllow = exclusionsApply(project);
  const hoursUsed = excludedHours(project);

  const startForm = (w: SuspectWindow) => {
    setOpenId(w.id);
    setReason(DEFAULT_REASON[w.check]);
    setNote('');
    setAck(false);
    setError(null);
  };

  const confirm = (w: SuspectWindow) => {
    const outcome = excludeWindow(project, amiData, windows, w, {
      reason, note, acknowledged: ack, userId, userLabel,
    });
    if (!outcome.ok) { setError(outcome.error); return; }
    onChange(outcome.updates);
    setOpenId(null);
  };

  const undo = (id: string) => {
    const outcome = removeExclusion(project, amiData, id);
    if (outcome.ok) onChange(outcome.updates);
  };

  /** Highest reading in effect outside the window: the peak if it were excluded too. */
  const elsewhere = (w: SuspectWindow) => {
    const s = Date.parse(w.start);
    const e = Date.parse(w.end);
    let kw = 0;
    for (const p of inEffect) {
      const t = Date.parse(p.timestamp);
      if ((t < s || t >= e) && p.kw > kw) kw = p.kw;
    }
    return kw;
  };

  return (
    <div className="mt-4 space-y-2" data-testid="suspect-peak-panel">
      {open.map(w => {
        const other = elsewhere(w);
        return (
          <div
            key={w.id}
            data-testid="suspect-window-card"
            data-check={w.check}
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 flex-shrink-0 text-amber-600" size={16} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{CHECK_LABELS[w.check]}</p>
                <p className="mt-0.5 leading-snug">{explain(w, project)}</p>
                <p className="mt-1 text-xs text-amber-800">
                  {fmtWhen(w.start)} – {fmtWhen(w.end)} ({fmtHours(w.hours)}) · peak inside{' '}
                  <strong data-testid="suspect-window-peak">{w.peakKw.toFixed(2)} kW</strong> · peak elsewhere{' '}
                  <strong>{other.toFixed(2)} kW</strong>
                  {w.peakKw <= other && ' · excluding it would not change the peak'}
                </p>
                {canExclude && !rulesAllow && (
                  <p className="mt-1 text-xs">This project was completed under an earlier revision of the calculation rules, which does not apply exclusions.</p>
                )}
                {canExclude && rulesAllow && openId !== w.id && (
                  <button
                    type="button"
                    data-testid="btn-exclude-window"
                    onClick={() => startForm(w)}
                    className="mt-1.5 text-xs font-semibold text-amber-900 underline hover:text-amber-700"
                  >
                    Exclude this window
                  </button>
                )}
                {openId === w.id && (
                  <div className="mt-2 space-y-2 rounded border border-amber-200 bg-white p-2.5 text-gray-800" data-testid="exclusion-form">
                    <label className="block text-xs font-medium">
                      Reason
                      <select
                        data-testid="exclusion-reason"
                        value={reason}
                        onChange={e => setReason(e.target.value as ExclusionReason)}
                        className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
                      >
                        {EXCLUSION_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </label>
                    <label className="block text-xs font-medium">
                      Note <span className="font-normal text-gray-500">(optional, prints on the report)</span>
                      <textarea
                        data-testid="exclusion-note"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        rows={2}
                        className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="flex items-start gap-2 text-xs">
                      <input
                        type="checkbox"
                        data-testid="exclusion-ack"
                        checked={ack}
                        onChange={e => setAck(e.target.checked)}
                        className="mt-0.5"
                      />
                      <span>{EXCLUSION_ACKNOWLEDGEMENT}</span>
                    </label>
                    {error && <p data-testid="exclusion-error" className="text-xs text-red-700">{error}</p>}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        data-testid="btn-confirm-exclusion"
                        disabled={!ack}
                        onClick={() => confirm(w)}
                        className="rounded bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                      >
                        Exclude
                      </button>
                      <button type="button" onClick={() => setOpenId(null)} className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {excluded.length > 0 && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700" data-testid="excluded-windows">
          <p className="font-semibold text-gray-900">
            Excluded from the calculation · {fmtHours(Math.round(hoursUsed * 10) / 10)} of {MAX_EXCLUDED_HOURS} h
          </p>
          <ul className="mt-1 space-y-1">
            {excluded.map(x => (
              <li key={x.id} data-testid="excluded-window-row" className="flex flex-wrap items-baseline gap-x-2">
                <span>{fmtWhen(x.start)} – {fmtWhen(x.end)}</span>
                <span className="text-gray-500">{reasonLabel(x.reason)} · {x.user_label}</span>
                {canExclude && (
                  <button
                    type="button"
                    data-testid="btn-undo-exclusion"
                    onClick={() => undo(x.id)}
                    className="inline-flex items-center gap-1 text-blue-700 underline hover:text-blue-900"
                  >
                    <Undo2 size={12} /> Undo
                  </button>
                )}
              </li>
            ))}
          </ul>
          {project.ami_exclusions && (
            <p className="mt-1 text-gray-500">
              Raw peak {project.ami_exclusions.raw_peak_kw.toFixed(2)} kW, kept on file and printed on the report beside the peak used.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
